import { CategoryRounded, PersonRemoveRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Dialogs } from "../components/dialogs";
import PageHeader from "../components/page-header";
import StickyHeadTable, { Column } from "../components/table";
import { Tema, UserStatusType } from "../types/general";
import { ViewModelUserTemas } from "../viewmodel/viewmodel-user-temas";

const STATUS_LABEL: Record<UserStatusType, string> = {
  PENDENTE: "Pendente",
  CONFIRMADA: "Confirmada",
  BLOQUEADA: "Bloqueada",
};

const PAGE_SIZE = 10;

function UserTemasPage() {
  const vm = ViewModelUserTemas();
  const [selected, setSelected] = useState<Tema[]>([]);
  const [page, setPage] = useState(1);

  // Paginação feita na tela; recua se a página actual ficar vazia ao remover temas
  const totalPages = Math.max(1, Math.ceil(vm.associated.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = vm.associated.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleAdd = async () => {
    if (await vm.addTemas(selected)) setSelected([]);
  };

  const columns: Column<Tema>[] = [
    { id: "nome", label: "Tema", minWidth: 260 },
    {
      id: "perguntas",
      label: "Perguntas",
      minWidth: 100,
      renderCell: (row) => Number(row.easy ?? 0) + Number(row.medium ?? 0) + Number(row.dificult ?? 0),
    },
    {
      id: "available",
      label: "Disponível",
      minWidth: 110,
      renderCell: (row) => (
        <Chip label={row.available ? "Sim" : "Não"} color={row.available ? "success" : "default"} size="small" />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      minWidth: 90,
      renderCell: (row) => (
        <Tooltip title="Remover tema do utilizador" arrow>
          <IconButton size="small" color="error" onClick={() => vm.removeTema(row)}>
            <PersonRemoveRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (vm.loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", mt: 12 }}>
        <CircularProgress />
        <Dialogs {...vm.dialog} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 2 }}>
      <PageHeader
        icon={<CategoryRounded />}
        color="#8B5CF6"
        title={`Temas de ${vm.managedUser?.name ?? ""}`}
        subtitle={
          vm.managedUser
            ? `${vm.managedUser.email_whatsapp} · ${STATUS_LABEL[vm.managedUser.status] ?? vm.managedUser.status}`
            : undefined
        }
        onBack={() => vm.navigate("/utilizadores")}
        backLabel="Utilizadores"
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          Associar temas
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-start" }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{ flex: 1, minWidth: 260 }}
            options={vm.available}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={(t) => t.nome}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Todos os temas já estão associados"
            disabled={vm.saving}
            renderInput={(params) => <TextField {...params} label="Escolha os temas a adicionar" />}
          />
          <Button
            variant="contained"
            size="large"
            onClick={handleAdd}
            disabled={vm.saving || selected.length === 0}
            sx={{ height: 56 }}
          >
            {vm.saving ? <CircularProgress size={22} color="inherit" /> : "Associar"}
          </Button>
        </Box>
      </Paper>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
        Temas associados ({vm.associated.length})
      </Typography>
      <StickyHeadTable
        columns={columns}
        rows={pageRows}
        getRowId={(row) => row.id!}
        pagination={{
          totalData: vm.associated.length,
          totalPages,
          currentPage,
          onPageChange: setPage,
          onRowsPerPageChange: () => {},
        }}
      />

      <Dialogs {...vm.dialog} />
    </Container>
  );
}

export default UserTemasPage;
